import {
  useCallback,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { ModuleNode } from '@/models/permission';
import { flattenModules } from '@/services/permission-service';

import { PermissionContext } from './context';

interface PermissionProviderProps {
  children: ReactNode;
  initialModules?: ModuleNode[];
}

const findModuleByName = (
  nodes: ModuleNode[],
  moduleName: string
): ModuleNode | undefined => {
  for (const node of nodes) {
    if (node.moduleName === moduleName) {
      return node;
    }

    if (node.children) {
      const found = findModuleByName(node.children, moduleName);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

const findModuleById = (
  nodes: ModuleNode[],
  moduleId: number
): ModuleNode | undefined => {
  for (const node of nodes) {
    if (node.moduleId === moduleId) {
      return node;
    }

    if (node.children) {
      const found = findModuleById(node.children, moduleId);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

const hasModuleByName = (nodes: ModuleNode[], moduleName: string): boolean => {
  return findModuleByName(nodes, moduleName) !== undefined;
};

export const PermissionProvider = ({
  children,
  initialModules = [],
}: PermissionProviderProps): ReactElement => {
  const [moduleTree, setModuleTree] = useState<ModuleNode[]>(
    () => initialModules
  );

  const permissions = useMemo(() => flattenModules(moduleTree), [moduleTree]);

  const setModules = useCallback((newModules: ModuleNode[]): void => {
    setModuleTree(newModules);
  }, []);

  const hasPermission = useCallback(
    (code: string): boolean => {
      return permissions.some(permission => permission.code === code);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (codes: string[]): boolean => {
      return codes.some(code => hasPermission(code));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (codes: string[]): boolean => {
      return codes.every(code => hasPermission(code));
    },
    [hasPermission]
  );

  const hasModule = useCallback(
    (moduleName: string | number): boolean => {
      if (typeof moduleName === 'number') {
        return moduleTree.some(module => module.moduleId === moduleName);
      }

      return hasModuleByName(moduleTree, moduleName);
    },
    [moduleTree]
  );

  const getModuleByName = useCallback(
    (name: string): ModuleNode | undefined => {
      return findModuleByName(moduleTree, name);
    },
    [moduleTree]
  );

  const getModuleById = useCallback(
    (id: number): ModuleNode | undefined => {
      return findModuleById(moduleTree, id);
    },
    [moduleTree]
  );

  const getChildModules = useCallback(
    (parentModuleName: string): ModuleNode[] => {
      return getModuleByName(parentModuleName)?.children || [];
    },
    [getModuleByName]
  );

  const value = useMemo(
    () => ({
      modules: moduleTree,
      permissions,
      error: null,
      setModules,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasModule,
      getModuleByName,
      getModuleById,
      getChildModules,
    }),
    [
      moduleTree,
      permissions,
      setModules,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasModule,
      getModuleByName,
      getModuleById,
      getChildModules,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
